import type { ReactNode } from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAdminSearchQuery } from '@/apis/extended/admin/adminWorkspaceApi';
import { formatMoney, humanizeKey } from './loanHealth';

type Props = {
  query: string;
  /** The Users page lists people itself; show only the other kinds. */
  hideUsers?: boolean;
  onNavigate?: () => void;
  dense?: boolean;
};

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ px: 2, display: 'block', lineHeight: 2.2 }}
      >
        {title}
      </Typography>
      <List dense disablePadding>
        {children}
      </List>
    </Box>
  );
}

/** People, properties, contracts and loan files matching one search. */
export function AdminSearchResultsPanel({
  query,
  hideUsers,
  onNavigate,
  dense,
}: Props) {
  const navigate = useNavigate();
  const term = query.trim();
  const { data, isFetching } = useAdminSearchQuery(term, {
    skip: term.length < 2,
  });

  const go = (path: string) => {
    onNavigate?.();
    navigate(path);
  };

  if (term.length < 2) return null;
  const users = hideUsers ? [] : data?.users ?? [];
  const total =
    users.length +
    (data?.properties.length ?? 0) +
    (data?.contracts.length ?? 0) +
    (data?.loanFiles.length ?? 0);

  return (
    <Paper variant="outlined" sx={{ mb: dense ? 0 : 2, py: 1 }}>
      {isFetching && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : total === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, py: 1 }}
        >
          {hideUsers
            ? `No properties, contracts or loan files match "${term}".`
            : `Nothing matches "${term}".`}
        </Typography>
      ) : (
        <>
          {users.length > 0 && (
            <Group title="People">
              {users.map((u) => (
                <ListItemButton
                  key={u._id}
                  onClick={() => go(`/admin/user/${u._id}`)}
                >
                  <ListItemText
                    primary={u.name}
                    secondary={[u.email, u.phone, humanizeKey(u.role)]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItemButton>
              ))}
            </Group>
          )}
          {(data?.loanFiles.length ?? 0) > 0 && (
            <Group title="Loan files">
              {data!.loanFiles.map((f) => (
                <ListItemButton
                  key={f._id}
                  onClick={() => go(`/admin/loan-pipeline/${f._id}`)}
                >
                  <ListItemText
                    primary={`${
                      f.flowProfile === 'refinance' ? 'Refinance' : 'Purchase'
                    } · ${f.applicantName || 'Unknown borrower'}`}
                    secondary={[f.address, humanizeKey(f.status)]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItemButton>
              ))}
            </Group>
          )}
          {(data?.contracts.length ?? 0) > 0 && (
            <Group title="Contracts">
              {data!.contracts.map((c) => (
                <ListItemButton
                  key={c._id}
                  onClick={() => go(`/admin/property/${c._id}`)}
                >
                  <ListItemText
                    primary={c.address || c.contractId}
                    secondary={[
                      c.buyerName && `Buyer ${c.buyerName}`,
                      humanizeKey(c.status),
                      formatMoney(c.price),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItemButton>
              ))}
            </Group>
          )}
          {(data?.properties.length ?? 0) > 0 && (
            <Group title="Properties">
              {data!.properties.map((p) => (
                <ListItemButton
                  key={p._id}
                  onClick={() => go(`/admin/product/${p._id}`)}
                >
                  <ListItemText
                    primary={p.address || 'Untitled property'}
                    secondary={[
                      p.ownerName,
                      p.transactionMode === 'refinance'
                        ? 'Refinance'
                        : 'Purchase',
                      formatMoney(p.price),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItemButton>
              ))}
            </Group>
          )}
        </>
      )}
    </Paper>
  );
}
